import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { NotificationData } from '../types';
import { useNotification } from '../contexts/NotificationContext';

interface NotificationItemProps {
  notification: NotificationData;
}

const NotificationCard = styled.div<{ type: string; isVisible: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 1rem 1.5rem;
  position: relative;
  border-left: 4px solid ${({ theme, type }) => {
    switch (type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.error;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.primary;
    }
  }};
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  transform: ${({ isVisible }) => isVisible ? 'translateX(0)' : 'translateX(100%)'};
  transition: all ${({ theme }) => theme.transitions.base};
  max-width: 100%;
  word-wrap: break-word;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const NotificationIcon = styled.div<{ type: string }>`
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
  color: ${({ theme, type }) => {
    switch (type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.error;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.primary;
    }
  }};
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 0.25rem 0;
`;

const NotificationMessage = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.4;
  /* メッセージは改行で構造を作っている（アップロードの内訳・容量超過の説明など）。
     既定の white-space では 1 段落に潰れて読めないため改行を反映する。 */
  white-space: pre-line;
`;

const CloseButton = styled.button`
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
  
  ${({ variant, theme }) => variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    
    &:hover {
      background: ${theme.colors.primaryHover};
    }
  ` : `
    background: ${theme.colors.surfaceSecondary};
    color: ${theme.colors.textPrimary};
    
    &:hover {
      background: ${theme.colors.border};
    }
  `}
`;

const getIcon = (type: string) => {
  switch (type) {
    case 'success':
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    case 'error':
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    case 'warning':
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      );
    default:
      return (
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      );
  }
};

const NotificationItem: React.FC<NotificationItemProps> = ({ notification }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { removeNotification } = useNotification();

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      removeNotification(notification.id);
    }, 200);
  };

  const handleActionClick = (action: () => void) => {
    action();
    handleClose();
  };

  return (
    <NotificationCard type={notification.type} isVisible={isVisible}>
      <NotificationHeader>
        <NotificationIcon type={notification.type}>
          {getIcon(notification.type)}
        </NotificationIcon>
        <NotificationContent>
          <NotificationTitle>{notification.title}</NotificationTitle>
          <NotificationMessage>{notification.message}</NotificationMessage>
        </NotificationContent>
        <CloseButton onClick={handleClose} aria-label="Close notification">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6.854 6l3.293-3.293a.5.5 0 00-.708-.708L6 5.293 2.707 2a.5.5 0 00-.708.708L5.293 6 2 9.293a.5.5 0 00.708.708L6 6.707l3.293 3.293a.5.5 0 00.708-.708L6.854 6z"/>
          </svg>
        </CloseButton>
      </NotificationHeader>
      
      {notification.actions && notification.actions.length > 0 && (
        <ActionsContainer>
          {notification.actions.map((action, index) => (
            <ActionButton
              key={index}
              variant={action.variant}
              onClick={() => handleActionClick(action.onClick)}
            >
              {action.label}
            </ActionButton>
          ))}
        </ActionsContainer>
      )}
    </NotificationCard>
  );
};

export default NotificationItem;